
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FinalCTA() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    tier: 'Elite Access',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('tier', formData.tier);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d63jo2j0723ekv2itdp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          tier: 'Elite Access',
          message: '',
        });
      } else {
        // Capture non‑2xx responses
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        setSubmitStatus('error');
      }
    } catch (err) {
      // Network or unexpected errors
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="apply" className="py-32 relative">
      <div className="max-w-[640px] mx-auto px-8 text-center space-y-12">
        <div className="space-y-8">
          <h2 className="text-5xl lg:text-6xl font-light leading-tight">
            {t('cta.title1')}
            <br />
            {t('cta.title2')}
          </h2>
          <p className="text-lg text-[#b8b8b8]">{t('cta.subtitle')}</p>
        </div>

        <div className="bg-[#1a1a1a] border-2 border-white/10 rounded-3xl p-10 text-left">
          <h3 className="text-2xl font-light mb-8 text-center">{t('cta.formTitle')}</h3>

          <form
            id="priority-access-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div>
              <label htmlFor="fullName" className="block text-sm text-[#b8b8b8] mb-2">
                {t('cta.fullName')} *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-5 py-3 bg-[#0f0f0f] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                placeholder={t('cta.namePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm text-[#b8b8b8] mb-2">
                {t('cta.email')} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-5 py-3 bg-[#0f0f0f] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                placeholder={t('cta.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm text-[#b8b8b8] mb-2">
                {t('cta.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-5 py-3 bg-[#0f0f0f] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                placeholder={t('cta.phonePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="tier" className="block text-sm text-[#b8b8b8] mb-2">
                {t('cta.tier')} *
              </label>
              <select
                id="tier"
                name="tier"
                required
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                className="w-full px-5 py-3 bg-[#0f0f0f] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#d4af37] transition-colors cursor-pointer"
              >
                {/* Use Unicode for the Euro sign to avoid JSX parsing issues */}
                <option value="Elite Access">
                  {t('pricing.elite')} - {'\u20AC'}490
                </option>
                <option value="Premium Concierge">
                  {t('pricing.premium')} - {'\u20AC'}1,490
                </option>
                <option value="Founders Circle">
                  {t('pricing.founders')} - {'\u20AC'}4,900+
                </option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm text-[#b8b8b8] mb-2">
                {t('cta.message')}
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-5 py-3 bg-[#0f0f0f] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#d4af37] transition-colors resize-none"
                placeholder={t('cta.messagePlaceholder')}
              ></textarea>
              <p className="text-xs text-[#666666] mt-2">
                {formData.message.length}/500 {t('cta.chars')}
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-white text-black text-base font-medium rounded-full hover:bg-[#d4af37] hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? t('cta.submitting') : t('cta.submit')}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-[#d4af37]/20 border border-[#d4af37] rounded-lg text-center">
                <p className="text-[#d4af37] text-sm">{t('cta.success')}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-center">
                <p className="text-red-400 text-sm">{t('cta.error')}</p>
              </div>
            )}
          </form>
        </div>

        <p className="text-sm italic text-[#666666]">{t('cta.limited')}</p>
      </div>
    </section>
  );
}
